import type { DashboardLayoutConfig } from '@/entities/state/model'

/**
 * Dynamic dashboard layout fixtures.
 *
 * The dashboard layout is now config-driven (resolved from the
 * `GET /configuration/states` response, see {@link DashboardLayoutConfig}), so
 * the UI must handle an open set of layouts rather than a single hardcoded one.
 * These fixtures capture a representative spread of the shapes the backend can
 * return so tests can exercise the dynamic renderer against each of them:
 *
 * | Variant                          | columnOrder.desktop | columnOrder.mobile | columns | mobileViewComponent        | bottom            |
 * | -------------------------------- | ------------------- | ------------------ | ------- | -------------------------- | ----------------- |
 * | {@link dashboardLayoutTwoColumn} | `default`           | `default`          | 2       | `default`                  | —                 |
 * | {@link dashboardLayoutThreeColumnWithBottom} | `default` | `default`       | 3       | `default`                  | 2 carousels       |
 * | {@link dashboardLayoutReversedMobileCarousel} | `reversed` | `reversed`    | 3       | `compactOrderCardCarousel` | 1 carousel        |
 * | {@link dashboardLayoutSingleColumn} | absent           | `default`          | 1       | `default`                  | —                 |
 *
 * Every `states` filter key below references a state `name` that exists in
 * {@link stateDefinitionConfigMock} so a config built from any of these layouts
 * stays free of `diagnoseStateConfig` referential issues.
 */

/**
 * Baseline two-column row layout — the closest analogue to the previous
 * hardcoded layout. No bottom region.
 */
export const dashboardLayoutTwoColumn: DashboardLayoutConfig = {
  middle: {
    columnOrder: { desktop: 'default', mobile: 'default' },
    columns: [
      {
        id: 'on-the-way',
        name: 'On The Way',
        states: ['OnTheWay', 'TempoOnTheWay', 'PartnerOnTheWay'],
        mobileViewComponent: 'default',
      },
      {
        id: 'arrived',
        name: 'Arrived',
        states: ['Arrived'],
        mobileViewComponent: 'default',
      },
    ],
  },
}

/**
 * Fullest layout: three middle columns plus a bottom region with two carousels.
 * Serves as the example dynamic layout for `npm run dev:mock`.
 */
export const dashboardLayoutThreeColumnWithBottom: DashboardLayoutConfig = {
  middle: {
    columnOrder: { desktop: 'default', mobile: 'default' },
    columns: [
      {
        id: 'on-the-way',
        name: 'On The Way',
        states: ['OnTheWay', 'TempoOnTheWay', 'PartnerOnTheWay'],
        mobileViewComponent: 'default',
      },
      {
        id: 'arrived',
        name: 'Arrived',
        states: ['Arrived'],
        mobileViewComponent: 'default',
      },
      {
        id: 'delivering',
        name: 'Delivering',
        states: ['DeliveringToCustomer'],
        mobileViewComponent: 'default',
      },
    ],
  },
  bottom: {
    enabled: true,
    components: [
      { id: 'ready-to-collect', type: 'carousel', states: ['ReadyToCollect'] },
      { id: 'ready-to-pick', type: 'carousel', states: ['ReadyToPick'] },
    ],
  },
}

/**
 * Reversed column order with compact carousels on mobile and a single bottom
 * carousel — exercises `columnOrder: 'row-reverse'` and the
 * `compactOrderCardCarousel` mobile renderer.
 */
export const dashboardLayoutReversedMobileCarousel: DashboardLayoutConfig = {
  middle: {
    columnOrder: { desktop: 'reversed', mobile: 'reversed' },
    columns: [
      {
        id: 'delivering',
        name: 'Delivering',
        states: ['DeliveringToCustomer'],
        mobileViewComponent: 'compactOrderCardCarousel',
      },
      {
        id: 'arrived',
        name: 'Arrived',
        states: ['Arrived'],
        mobileViewComponent: 'compactOrderCardCarousel',
      },
      {
        id: 'on-the-way',
        name: 'On The Way',
        states: ['OnTheWay', 'TempoOnTheWay', 'PartnerOnTheWay'],
        mobileViewComponent: 'default',
      },
    ],
  },
  bottom: {
    enabled: false,
    components: [],
  },
}

/**
 * Single stacked column with `columnOrder: 'column'` and no bottom region —
 * the minimal valid layout that still renders the dashboard.
 */
export const dashboardLayoutSingleColumn: DashboardLayoutConfig = {
  middle: {
    columnOrder: { mobile: 'default' },
    columns: [
      {
        id: 'active',
        name: 'Active Orders',
        states: ['OnTheWay', 'TempoOnTheWay', 'PartnerOnTheWay', 'Arrived', 'DeliveringToCustomer'],
        mobileViewComponent: 'default',
      },
    ],
  },
}

export interface NamedDashboardLayout {
  /** Human-readable label used to title parametrised test cases. */
  name: string
  layout: DashboardLayoutConfig
}

/**
 * The four dynamic layout variants, ready to feed a `describe.each` / `forEach`
 * so the dashboard is verified against multiple layouts rather than one
 * hardcoded config.
 */
export const dashboardLayoutVariants: NamedDashboardLayout[] = [
  { name: 'two-column row layout', layout: dashboardLayoutTwoColumn },
  { name: 'three-column layout with bottom carousels', layout: dashboardLayoutThreeColumnWithBottom },
  { name: 'reversed layout with compact mobile carousels', layout: dashboardLayoutReversedMobileCarousel },
  { name: 'single stacked column layout', layout: dashboardLayoutSingleColumn },
]
